package frontend.pages

import AccountSelect
import ErrorString
import StatusBadge
import StatusBadgeRefresh
import androidx.compose.runtime.*
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.Party
import net.sergeych.ugdx.shared.*
import net.sergeych.ugdx.shared.api.*
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import components.*
import frontend.App
import frontend.lib.DateTimeDisplay
import frontend.lib.classList
import frontend.lib.displayCurrencyCode
import frontend.modals.InvoicePayModal
import frontend.models.User
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.InternalCoroutinesApi
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.*

private val paidColor = "bg-green-100 text-green-800 hover:bg-green-200"
private val pendingColor = "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
private val rejectedColor = "bg-red-100 text-red-800 hover:bg-red-200"



@Composable
fun InvoiceRow(context: InvoicesContext, signedInvoice: SignedInvoice) {
    val scope = rememberCoroutineScope()
    val invoice = signedInvoice.invoice
    var isPayModalOpened by remember { mutableStateOf(false) }
    var isLoading by mutableStateOf(false)
    val myId = context.user.userAccount.partyId
    val issuedToId = invoice.issuedToPartyId
    val issuerId = invoice.issuerPartyId
    val isFinalState = signedInvoice.isPaid || signedInvoice.isRejected

    fun amount(): String {
        return "${invoice.total.toPlainString()} ${displayCurrencyCode(invoice.currencyCode)}"
    }

    suspend fun refresh() {
        context.refresh(signedInvoice)
    }

    @Composable
    fun actions() {
        val canReject = (issuedToId == myId || issuerId == myId) && !isFinalState
        val canPay = issuedToId == myId && !isFinalState

        fun reject() {
            scope.launch {
                signedInvoice.reject()
                refresh()
            }
        }
        fun pay() { isPayModalOpened = true }
        fun details() { context.open(invoice.uniqueId) }

        var rejectStyle = "border-gray-300 bg-red-50 text-red-700 hover:bg-red-200 rounded-r-md"
        var payStyle = "border-gray-300 bg-green-50 text-green-700 hover:bg-green-200 rounded-l-md"

        if (!canPay) { rejectStyle += " rounded-l-md" }
        if (!canReject) { payStyle += " rounded-r-md" }

        Span(attrs = { classList("relative z-0 inline-flex shadow-sm rounded-md") }) {
            if (canPay) Button(attrs = {
                type(ButtonType.Button)
                classList("relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium $payStyle")
                onClick { pay() }
            }) { Text("Pay") }

            if (canReject) Button(attrs = {
                type(ButtonType.Button)
                classList("-ml-px relative inline-flex items-center px-4 py-2 border text-sm font-medium $rejectStyle")
                onClick { reject() }
            }) { Text("Reject") }
        }
    }

    Tr() {
        Cell { Text(DateTimeDisplay(signedInvoice.createdAt)) }
        Cell { Span(attrs = { classList("text-lg font-bold") }) { Text(amount()) } }
        Cell { Text(signedInvoice.invoice.title) }
        Cell {
            InvoiceStatus(signedInvoice, context.user) { refresh() }
        }
        Cell {
            actions()
        }
        Cell { TLink("Details") { context.open(invoice.uniqueId) } }
    }

    if (isPayModalOpened) InvoicePayModal(context, signedInvoice) {
        scope.launch {
            refresh()
            isPayModalOpened = false
        }
    }
}

enum class InvoiceFilter(val title: String, val filter: (list: List<SignedInvoice>, user: User) -> List<SignedInvoice>) {
    CREATED_BY_ME("Created by me", { list, user ->
        list.filter { it.invoice.issuerPartyId == user.userAccount.partyId }
    }),
    UNPAID("Unpaid", { list, user ->
        list.filter { it.invoice.issuedToPartyId == user.userAccount.partyId && !it.isPaid }
    }),
    ARCHIVE("Archived", { list, user ->
        list.filter { it.isPaid || it.isRejected }
    })
}


@Composable
fun InvoiceFilterNav(context: InvoicesContext) {
    val activeTextClass = "border-indigo-500 text-indigo-600"
    val disactiveTextClass = "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200"
    val activeBadgeClass = "bg-indigo-100 text-indigo-600"
    val disactiveBadgeClass = "bg-gray-100 text-gray-900"

    fun calculate(filter: InvoiceFilter): Int {
        return filter.filter(context.invoices, context.user).size
    }

    fun textClass(filter: InvoiceFilter): String {
        return if (filter == context.invoicesFilter) activeTextClass else disactiveTextClass
    }

    fun badgeClass(filter: InvoiceFilter): String {
        return if (filter == context.invoicesFilter) activeBadgeClass else disactiveBadgeClass
    }

    Div(attrs = { classList("border-b border-gray-200") }) {
        Nav(attrs = {
            classList("-mb-px flex space-x-8")
            attr("aria-label", "Tabs")
        }) {
            for (filter in InvoiceFilter.values()) {
                A(href="javascript:void(0)", attrs = {
                    classList("${textClass(filter)} whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm")
                    onClick {
                        if (filter != context.invoicesFilter) context.invoicesFilter = filter
                    }
                }) {
                    Text(filter.title)
                    Span(attrs = {
                        classList("${badgeClass(filter)} hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block")
                    }) {
                        Text(calculate(filter).toString())
                    }
                }
            }
        }
    }
}


@Composable
fun InvoicesUI(context: InvoicesContext) {
    val user = context.user

    fun createInvoice() {
        context.newInvoice = true
    }

    val invoices = context.invoicesFilter.filter(context.invoices, context.user)

//    H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
//        Text("Invoices")
//    }

    InvoiceFilterNav(context)

    if (invoices.isEmpty()) {
        P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
            Text("You have no invoices yet. ")
            A("javascript:void(0);", attrs = {
                classes("font-medium", "text-indigo-600", "hover:text-indigo-500")
                onClick { createInvoice() }
            }) {
                Text("Create one")
            }
        }
    } else {
        Table(attrs = { classList("min-w-full divide-y divide-gray-200") }) {
            Thead(attrs = { classList("bg-gray-50") }) {
                Tr() {
                    HeaderCell { Text("Created At") }
                    HeaderCell { Text("Amount") }
                    HeaderCell { Text("Title") }
                    HeaderCell { Text("Status") }
                    HeaderCell { Text("Actions") }
                    HeaderCell { Text("Details") }
                }
            }

            Tbody {
                invoices.forEach { InvoiceRow(context, it) }

                Tr() {
                    Td(attrs = { classList("px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900") }) {
                        A("javascript:void(0);", attrs = {
                            classes("font-medium", "text-indigo-600", "hover:text-indigo-500")
                            onClick { createInvoice() }
                        }) {
                            Text("Create invoice")
                        }
                    }
                }
            }
        }
    }

//    if (isCreationModalOpen) CreateAccountModal(wallet) { isCreationModalOpen = false }
}

@Composable
fun InvoiceItemEditable(item: Invoice.Item?, onFill: (data: Invoice.Item?) -> Unit) {
    var name: String by mutableStateOf(item?.name ?: "")
    var qty: BigDecimal? by mutableStateOf(item?.qty)
    var price: BigDecimal? by mutableStateOf(item?.price)
    var amount: BigDecimal? by mutableStateOf(item?.amount)

    fun checkFill(name: String, qty: BigDecimal?, price: BigDecimal?, amount: BigDecimal?) {
        if (name != "" && qty != null && price != null && amount != null) {
            onFill(Invoice.Item(name, qty, price, amount))
        } else onFill(null)
    }

    Tr {
        Cell { TTextInput("name", name) {
            onChange {
                name = it
                checkFill(name, qty, price, amount)
            }
            onInput { name = it }
        } }
        Cell { TDecimalInput("qty", qty) {

            onChange {
                qty = it
                price?.let {
                    val p = it
                    qty?.let {
                        amount = p * it
                    }
                }
                checkFill(name, qty, price, amount)
            }
        } }
        Cell { TDecimalInput("price", price) {

            onChange {
                price = it
                price?.let {
                    val p = it
                    qty?.let {
                        amount = p * it
                    }
                }
                checkFill(name, qty, price, amount)
            }
        } }
        Cell { TDecimalInput("amount", amount) {
            disabled()
        } }
    }
}

@Composable
fun InvoiceItemsEditalbe(onChange: (items: List<Invoice.Item>) -> Unit) {
    var items = remember { mutableStateListOf<Invoice.Item>() }

    fun onFillExisting(item: Invoice.Item?, index: Int) {
        if (item == null) items.removeAt(index)
        else { items.set(index, item) }
        println("run callback ${items.size}")
        onChange(items)
    }

    fun onFill(item: Invoice.Item?) {
        item?.let {
            items.add(it)
            println("run callback ${items.size}")
            onChange(items)
        }
    }

    Table(attrs = { classList("min-w-full divide-y divide-gray-200 mt-2") }) {
        Thead(attrs = { classList("bg-gray-50") }) {
            Tr() {
                HeaderCell { Text("Name") }
                HeaderCell { Text("Quantity") }
                HeaderCell { Text("Price") }
                HeaderCell { Text("Amount") }
            }
        }
        Tbody {
            for (item in items) {
                val idx = items.indexOf(item)
                key(idx) {
                    InvoiceItemEditable(item) { onFillExisting(it, idx) }
                }
            }
            InvoiceItemEditable(null) { onFill(it) }
        }
    }
}


@Composable
fun CreateInvoiceUI(context: InvoicesContext) {
    val scope = rememberCoroutineScope()
    var title by remember { mutableStateOf("") }
    var toAccount: ApiAccount? by remember { mutableStateOf(context.user.accounts.firstOrNull()) }
    var issuedTo: String by remember { mutableStateOf("") }
    var issuedToRaw: String by remember { mutableStateOf("") }
    var party: Party? by remember { mutableStateOf(null) }
    var items = remember { mutableListOf<Invoice.Item>() }
    var issuedToError by remember { mutableStateOf("") }
    var total by remember { mutableStateOf(BigDecimal.ZERO) }
    var firstLaunch by remember { mutableStateOf(true) }
    var isCreatingInvoice by remember { mutableStateOf(false) }
    var creatingError by remember { mutableStateOf("") }

    fun createInvoice(
        title: String,
        toAccount: ApiAccount?,
        party: Party?,
        items: List<Invoice.Item>
    ) {
        if (title == "") { creatingError = "Title can't be empty"; return }
        if (toAccount == null) { creatingError = "Destination account can't be empty"; return }
        if (party == null) { creatingError = "Issued to can't be empty"; return }
        if (items.size == 0) { creatingError = "You need to add at least one item"; return }

        isCreatingInvoice = true

        GlobalScope.launch {
            val currency = context.app.currencies.find { it.code == toAccount.currencyCode }
            currency?.let {
                try {
                    val signedInvoice = context.user.userAccount.createInvoice(title, toAccount, party, it, items)
                    context.app.router.dispatch("/invoices/${signedInvoice.invoice.uniqueId}")
                    isCreatingInvoice = false
                    context.newInvoice = false
                } catch(e: ApiException) {
                    creatingError = e.text
                    isCreatingInvoice = false
                }
            }
        }
    }

    LaunchedEffect(issuedTo) {
        if (!firstLaunch) {
            issuedToError = ""
            try {
                party = context.user.userAccount.getPartyByLoginName(issuedTo)
                if (party == null) issuedToError = "User not found"
            } catch (e: ApiException) {
                issuedToError = e.text
            }
        }
    }

    Div(attrs = { classList("bg-white shadow overflow-scroll sm:rounded-lg") }) {
        Div(attrs = { classList("px-4 py-5 sm:px-6") }) {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Create new invoice")
            }
            P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
                TLink("Back to invoices") {
                    context.newInvoice = false
                    window.location.hash = "#/invoices"
                }
            }
        }
        Div(attrs = { classList("border-t border-gray-200 px-4 py-5 sm:px-6") }) {
            Form {
                Div(attrs = { classList("mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6") }) {
                    Div(attrs = { classList("sm:col-span-2") }) {
                        TTextInput("title", title) {
                            onInput { title = it }
                            label { Text("Title") }
                        }
                    }
                    Div(attrs = { classList("sm:col-span-2") }) {
                        TTextInput("issuedTo", issuedToRaw) {
                            onInput { issuedToRaw = it }
                            onChange {
                                firstLaunch = false
                                issuedTo = it
                            }
                            label { Text("Issued to (login name)") }
                        }
                        if (issuedToError != "") ErrorString(issuedToError)
                    }
                    Div(attrs = { classList("sm:col-span-2") }) {
                        AccountSelect("recipientAccount", toAccount) {
                            label { Text("Select destination account") }
                            options(context.user.accounts)
                            onChange { it?.let { toAccount = it } }
                        }
                    }
                }

                Div(attrs = { classList("mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6") }) {
                    Div(attrs = { classList("sm:col-span-6") }) {
                        Label(forId = "items", attrs = {
                            classes("block", "text-sm", "font-medium", "text-gray-700")
                        }) { Text("Items") }
                        InvoiceItemsEditalbe {
                            items = it.toMutableList()
                            total = items.map { it.amount }.reduce(BigDecimal::add)
                        }
                    }
                }
                Div(attrs = { classList("mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6") }) {
                    Div(attrs = { classList("sm:col-span-6") }) {
                        H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                            Text("TOTAL: ${total.toPlainString()} ${toAccount?.currencyCode?.let(::displayCurrencyCode)}")
                        }
                    }
                }

                if (creatingError != "") ErrorString(creatingError)

                Div(attrs = { classList("mt-4") }) {
                    TButton(attrs = {
                        color(ButtonColor.Green)
                        size(ButtonSize.lg)
                        onClick { createInvoice(title, toAccount, party, items) }
                        isLoading(isCreatingInvoice)
                    }) { Text("Create invoice") }
                }
            }
        }
    }
}

@Composable
fun DKey(content: @Composable () -> Unit) {
    Dt(attrs = { classList("text-sm font-medium text-gray-500") }) {
        content()
    }
}

@Composable
fun DValue(content: @Composable () -> Unit) {
    Dd(attrs = { classList("mt-1 text-sm text-gray-900") }) {
        content()
    }
}

@Composable
fun InvoiceItem(item: Invoice.Item) {
    Tr {
        Cell { Text(item.name) }
        Cell { Text(item.qty.toPlainString()) }
        Cell { Text("${item.price.toPlainString()}")}
        Cell { Text("${item.amount.toPlainString()}")}
    }
}

@Composable
fun InvoiceStatus(sInvoice: SignedInvoice, user: User, refresh: suspend () -> Unit) {
    val invoice = sInvoice.invoice
    val myId = user.userAccount.partyId
    val issuedToId = invoice.issuedToPartyId
    val issuerId = invoice.issuerPartyId
    val isFinalState = sInvoice.isPaid || sInvoice.isRejected

    if (!isFinalState) {
        if (issuedToId == myId)
            StatusBadge(pendingColor, "Unpaid")
        else
            StatusBadgeRefresh(pendingColor, "Pending", { refresh() })
    } else {
        sInvoice.paidAt?.let {
            StatusBadge(paidColor, "Paid at ${DateTimeDisplay(it)}")
        }
        sInvoice.rejectedAt?.let {
            StatusBadge(rejectedColor, "Rejected at ${DateTimeDisplay(it)}")
        }

    }
}


@Composable
fun InvoiceUI(context: InvoicesContext, invoiceId: String) {
    val scope = rememberCoroutineScope()
    var signedInvoice: SignedInvoice? by mutableStateOf(null)

    LaunchedEffect(true) {
        signedInvoice = SignedInvoice.get(invoiceId)
    }

    suspend fun refresh() {
        signedInvoice?.let { signedInvoice = it.refresh() }
    }

    Div(attrs = { classList("bg-white shadow overflow-scroll sm:rounded-lg") }) {
        Div(attrs = { classList("px-4 py-5 sm:px-6") }) {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text(signedInvoice?.invoice?.title ?: invoiceId)
            }
            P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
                TLink("Back to invoices") {
                    context.app.router.dispatch("/invoices")
                }
            }
        }
        signedInvoice?.let {
            val i = it.invoice
            Div(attrs = { classList("border-t border-gray-200 px-4 py-5 sm:px-6") }) {
                Dl(attrs = { classList("grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2") }) {
                    Div(attrs = { classList("sm:col-span-1") }) {
                        DKey { Text("Created at") }
                        DValue { Text(DateTimeDisplay(it.createdAt)) }
                    }
                    Div(attrs = { classList("sm:col-span-1") }) {
                        DKey { Text("Status") }
                        DValue { InvoiceStatus(it, context.user) { refresh() } }
                    }

                    Div(attrs = { classList("sm:col-span-1") }) {
                        DKey { Text("Due by") }
                        DValue { Text(DateTimeDisplay(i.dueBy)) }
                    }
                    Div(attrs = { classList("sm:col-span-1") }) {
                        DKey { Text("Total") }
                        DValue {
                            Text("${i.total.toPlainString()} ${displayCurrencyCode(i.currencyCode)}")
                        }
                    }

                    Div(attrs = { classList("sm:col-span-2") }) {
                        DKey { Text("Items") }
                        DValue {
                            Table(attrs = { classList("min-w-full divide-y divide-gray-200 mt-2") }) {
                                Thead(attrs = { classList("bg-gray-50") }) {
                                    Tr() {
                                        HeaderCell { Text("Name") }
                                        HeaderCell { Text("Quantity") }
                                        HeaderCell { Text("Price") }
                                        HeaderCell { Text("Amount") }
                                    }
                                }
                                Tbody {
                                    for (item in i.items) {
                                        val idx = i.items.indexOf(item)
                                        key(idx) {
                                            InvoiceItem(item)
                                        }
                                    }
                                }
                            }
                        }
                    }
                    it.operation?.let {
                        Div(attrs = { classList("sm:col-span-1") }) {
                            DKey { Text("Operation") }
                            DValue {
                                OperationsList(context.app, context.user, listOf(it))
                            }
                        }
                    }
                }


            }
        }
    }
}

//fun getInvoiceId(): String? {
//    val hash = window.location.hash
//    val parts = hash.split("/")
//    if (parts.size > 2) return parts.last()
//
//    var search = window.location.search
//    if (search.length > 0) {
//        var
//    }
//    return null
//}


class InvoicesContext(val app: App, val user: User) {
    var invoices = mutableStateListOf<SignedInvoice>()
//    var invoiceId by mutableStateOf(app.routeInvoiceId)
    var newInvoice by mutableStateOf(false)
    var invoicesFilter by mutableStateOf(InvoiceFilter.CREATED_BY_ME)

    suspend fun refresh(signedInvoice: SignedInvoice) {
        val updated = signedInvoice.refresh()
        invoices.set(invoices.indexOf(signedInvoice), updated)
    }

    fun open(newInvoiceId: String) {
        app.router.dispatch("/invoices/${newInvoiceId}")
//        window.location.hash = "${window.location.hash}/${newInvoiceId}"
//        app.routeInvoiceId = newInvoiceId
    }
}


@Composable
fun InvoicesScreenUI(app: App, user: User, params: MatchResult? = null) {
    val scope = rememberCoroutineScope()
    val context = remember { InvoicesContext(app, user) }
    var invoiceId: String? = null
    var login: String? = null
    var isLoading by remember { mutableStateOf(true) }

    if (params != null) {
        var (invoiceIdParam, loginParam) = params.destructured
        invoiceId = invoiceIdParam
        login = loginParam
    }

    LaunchedEffect(true) {
        login?.let { if (login != "" && user.userAccount.loginName != it) GuestAPI().signOut() }
        user.load()
        val x = SignedInvoice.all()
        x.onEach { context.invoices.add(it) }.collect()
        isLoading = false
    }

    if (!isLoading) {
        if (invoiceId != null) invoiceId?.let { InvoiceUI(context, it) }
        else if (context.newInvoice == true) CreateInvoiceUI(context)
        else RoundedContainer() { InvoicesUI(context) }
    }


//    if (!wallet.isLoading) RoundedContainer() {
//        wallet.selectedAccount?.let { AccountUI(app, wallet, it) }
//        if (wallet.selectedAccount == null) { Accounts(app, wallet) }
//    } else scope.launch {
//        println("Load user and wallet")
//        wallet.load()
//    }
}
